/** Generation + batch endpoints (PHASE3-CONTRACT §2/§3). Runs eddkit in-process server-side. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  BatchIn,
  EmailEddsIn,
  EmailEddsResult,
  GenerateIn,
  GenerationJob,
} from "./types";

export function useGenerate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: GenerateIn): Promise<GenerationJob> => {
      const { data } = await api.post<GenerationJob>("/generate", payload);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["generation-jobs"] });
    },
  });
}

export function useBatchGenerate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: BatchIn): Promise<GenerationJob> => {
      const { data } = await api.post<GenerationJob>("/generate/batch", payload);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["generation-jobs"] });
    },
  });
}

export function useGenerationJobs(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ["generation-jobs", params ?? {}],
    queryFn: async (): Promise<GenerationJob[]> => {
      const { data } = await api.get<GenerationJob[]>("/generate/jobs", {
        params: {
          limit: params?.limit ?? 50,
          offset: params?.offset ?? 0,
        },
      });
      return data;
    },
  });
}

export function useGenerationJob(id: string | undefined) {
  return useQuery({
    queryKey: ["generation-job", id],
    queryFn: async (): Promise<GenerationJob> => {
      const { data } = await api.get<GenerationJob>(`/generate/jobs/${id}`);
      return data;
    },
    enabled: !!id,
    // In worker-queue mode a run starts queued/running and only later flips to a terminal status.
    // Poll while it's in-flight (mirrors useQueryDefTask) so the detail/expanded view actually
    // updates; stop once it reaches a terminal state.
    refetchInterval: (q) =>
      ["queued", "running", "pending"].includes((q.state.data?.status as string) ?? "") ? 2500 : false,
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.delete(`/generate/jobs/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["generation-jobs"] });
    },
  });
}

/**
 * Email selected deliverables to one or more recipients (Phase 6.5). The server
 * attaches the rendered bundles and sends via the configured SMTP/provider; when no
 * provider is configured it returns `configured:false` (a friendly dev no-op) so the
 * UI can explain that email isn't set up yet.
 */
export function useEmailEdds() {
  return useMutation({
    mutationFn: async (payload: EmailEddsIn): Promise<EmailEddsResult> => {
      const { data } = await api.post<EmailEddsResult>("/generate/email", payload);
      return data;
    },
  });
}

/**
 * Resolve the generation-item ids for a set of jobs. The history list endpoint omits
 * `items` (summary only), so to email we fetch each job's detail and flatten its items
 * that actually produced a downloadable result. Used by the "Email selected" flow.
 */
export async function fetchJobItemIds(jobIds: string[]): Promise<string[]> {
  const details = await Promise.all(
    jobIds.map(async (id) => {
      const { data } = await api.get<GenerationJob>(`/generate/jobs/${id}`);
      return data;
    }),
  );
  return details.flatMap((job) =>
    (job.items ?? []).filter((i) => i.files.length > 0).map((i) => i.id),
  );
}

// ── authenticated downloads ────────────────────────────────────────────────────
// Downloads need the Bearer header, so we fetch the bundle as a blob through the
// shared axios `api` instance (the interceptor attaches the JWT) and trigger a
// browser save. The filename comes from the Content-Disposition header when present.

function filenameFromDisposition(header: unknown, fallback: string): string {
  if (typeof header !== "string") return fallback;
  // RFC 5987 (filename*) first, then plain filename=
  const star = /filename\*=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      return star[1];
    }
  }
  const plain = /filename=["']?([^"';]+)["']?/i.exec(header);
  return plain?.[1] ?? fallback;
}

async function downloadBlob(path: string, fallbackName: string): Promise<void> {
  const res = await api.get<Blob>(path, { responseType: "blob" });
  const name = filenameFromDisposition(res.headers["content-disposition"], fallbackName);
  const url = URL.createObjectURL(res.data);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke on the next tick so the click has a chance to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** Download the whole job bundle (.zip of all items + MANIFEST/BATCH-REPORT). */
export async function downloadJobBundle(jobId: string, name = "edd"): Promise<void> {
  await downloadBlob(`/generate/jobs/${jobId}/download`, `${name}.zip`);
}

/** Download a single item's bundle (.zip). */
export async function downloadItemBundle(
  jobId: string,
  itemId: string,
  name = "item",
): Promise<void> {
  await downloadBlob(`/generate/jobs/${jobId}/items/${itemId}/download`, `${name}.zip`);
}
